import { Rectangle, Sprite, Texture } from "pixi.js"

import { logger } from "../../../../logger"
import { PixiJsClientRenderer } from "../pixijs_client_renderer"

// Alpha at or below this counts as empty. Matches the threshold the death-crumble effect uses for
// the same question ("where does the art actually stop").
const CONTENT_ALPHA_THRESHOLD = 8

// Keyed by the caller's texture id. A composite tile texture is built once and shared by every
// sprite drawn from it, so its content bounds are worth computing once for the whole session --
// the readback is a GPU stall, and a null result (no renderer, e.g. in tests) is cached too so a
// failing lookup isn't retried on every sprite.
const cache: Map<string, Rectangle | null> = new Map()

// The tight bounds of a texture's non-transparent pixels, in the texture's own pixel space.
//
// This exists because a COMPOSITE tile texture is the bounding box of all the tiles making up an
// object (see TileTextureExtractor), and that box is routinely much wider than the thing you can
// see: the ember campfire's box carries ~32px of empty tile to its left and right. Anything sized
// from the texture rather than its content -- a hover/hit region above all -- is then wrong by that
// much, and the entity reacts to a pointer that is nowhere near it.
export const textureContentBounds = (texture: Texture, cacheKey: string): Rectangle | null => {
    if (cache.has(cacheKey)) {
        return cache.get(cacheKey)
    }
    const bounds = computeContentBounds(texture)
    cache.set(cacheKey, bounds)
    return bounds
}

const computeContentBounds = (texture: Texture): Rectangle | null => {
    const extract = (PixiJsClientRenderer._app?.renderer as any)?.plugins?.extract
    if (!extract || !texture?.valid) {
        return null
    }
    const probe = new Sprite(texture)
    try {
        const canvas: HTMLCanvasElement = extract.canvas(probe)
        const ctx = canvas.getContext("2d")
        const canvasWidth = canvas.width
        const canvasHeight = canvas.height
        if (!ctx || canvasWidth === 0 || canvasHeight === 0) {
            return null
        }
        const data = ctx.getImageData(0, 0, canvasWidth, canvasHeight).data

        let minX = canvasWidth
        let minY = canvasHeight
        let maxX = -1
        let maxY = -1
        for (let y = 0; y < canvasHeight; y++) {
            const rowOffset = y * canvasWidth * 4
            for (let x = 0; x < canvasWidth; x++) {
                if (data[rowOffset + x * 4 + 3] > CONTENT_ALPHA_THRESHOLD) {
                    if (x < minX) minX = x
                    if (x > maxX) maxX = x
                    if (y < minY) minY = y
                    if (y > maxY) maxY = y
                }
            }
        }
        if (maxX < 0) {
            // Entirely transparent (a blank stand-in marker). No content to point at.
            return null
        }

        // The extracted canvas can be at a different resolution than the texture, so map back through
        // its own dimensions rather than assuming 1:1.
        const scaleX = (texture.orig?.width ?? texture.width) / canvasWidth
        const scaleY = (texture.orig?.height ?? texture.height) / canvasHeight
        return new Rectangle(
            minX * scaleX,
            minY * scaleY,
            (maxX - minX + 1) * scaleX,
            (maxY - minY + 1) * scaleY,
        )
    } catch (e) {
        logger.error({ tags: ["sprite", "error"] }, e)
        return null
    } finally {
        probe.destroy()
    }
}
